import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useGetPageBySlugQuery } from "@/features/api/pageApi";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

const PageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPageBySlugQuery(slug);

  useEffect(() => {
    if (error) {
      toast.error("Failed to load page");
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.page) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl text-red-500">❌ Page not found</h2>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const page = data.page;

  const featuredImageUrl =
    typeof page.featuredImage === "string"
      ? page.featuredImage
      : page.featuredImage?.url || "";

  const featuredImageAlt =
    typeof page.featuredImage === "object"
      ? page.featuredImage?.altText || "Featured image"
      : "Featured image";

  const sanitizedHtml = DOMPurify.sanitize(page.content || "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "src",
      "width",
      "height",
    ],
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO */}
      <Helmet>
        <title>{page.title}</title>
        <meta
          name="description"
          content={page.metaDescription || page.title}
        />
      </Helmet>

      {/* Main Content */}
      <main className="flex-grow px-5 sm:px-6 md:px-8 lg:px-10 xl:px-20 max-w-screen-xl mx-auto mt-5 pb-10">
        {/* Breadcrumb */}
        <div className="mb-4 text-sm text-gray-500">
          <Link to="/" className="hover:underline">
            Home
          </Link>
          <span className="mx-2">/</span>
          <span>{page.title}</span>
        </div>

        {/* Page Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
          {page.title}
        </h1>

        {/* Featured Image */}
        {featuredImageUrl && (
          <div className="mb-8 flex justify-center">
            <img
              src={featuredImageUrl}
              alt={featuredImageAlt}
              className="w-1/3 h-auto rounded-lg shadow-xl object-cover"
            />
          </div>
        )}

        {/* Page Content */}
        <div className="prose max-w-none">
          {sanitizedHtml.trim() ? parse(sanitizedHtml) : <p>This page has no content yet.</p>}
        </div>
      </main>
    </div>
  );
};

export default PageView;
